import Controller, { OnClick, OnMount, OnUnmount } from '@philips-internal/oswald-ui-core/lib/client';
import { ActionAPI, FieldAPI, FormAPI, ModalProps, RadioGroupAPI } from '@philips-internal/oswald-ui-components/lib/components';
import { applicationContext, foundationContext } from '@philips-internal/oswald-ui-foundation';
import { HttpService } from '@philips-internal/oswald-ui-core/lib/services';
import { Placement, ToastLevel, ToastType } from '@philips-internal/oswald-ui-constants';
import { ModalService, SelectableItem, ToastService } from '@philips-internal/oswald-ui-components';
import { Value } from '@philips-internal/oswald-ui-core';
import { generateUUID } from '@philips-internal/oswald-ui-utils';
import { DATE_PATTERN, FULL_LOCALE_DATE, MONTH_YEAR_FORMAT } from '../../../../constants/date-utils';

import { Inject } from '@odinjs/odin';
import moment from 'moment';

import InformationBarUtils from '../../../commons/header/information-bar-header-utils';
import { FragmentNames, HealthHistoryAllergiesViewerAliases } from '../../../../codegen';
import BaseController from '../../../BaseController';
import { HealthHistoryListContext } from '../../service/health-history-list-service';
import { HealthHistoryAllergiesResponse, HealthHistoryType } from '../../health-history-types';
import { Endpoint } from '../../../../constants/endpoints';
import { BooleanUtils } from '../../../../utils/boolean-utils';
import { AllergiesRadioGroupExpressions, AllergiesRadioGroupId } from '../radio-group-data/allergies-radio-group-expressions';
import { ApplicationRoute } from '../../../../constants/routes';
import { AllergiesDateTypeEntry, AllergiesDateTypeId } from '../autocomplete-data/allergies-autocomplete-expressions';
import { FoundationUtils } from '../../../../utils/foundation-utils';
import { FunctionName, PEP_281Parameters } from '../../../../utils/parameters';
import DialogService from '../../../../services/dialog-service';
import HealthHistoryAllergiesCommons from '../health-history-allergies-commons/health-history-allergies-commons';
import { HealthProfessionalUser } from '../../../../app/authentication';

enum VerifyReviewParameter {
  ALLOWED_REVIEW = 'S',
  NOT_ALLOWED_REVIEW = 'N'
}

@Controller({ fragment: FragmentNames.HEALTH_HISTORY_ALLERGIES_VIEWER })
export default class HealthHistoryAllergiesViewerController extends BaseController {

  @Inject(HealthHistoryAllergiesViewerAliases.ACTION_NEW_HEALTH_HISTORY_ALLERGIES)
  private readonly actionNewHealthHistoryAPI!: ActionAPI

  @Inject(HealthHistoryAllergiesViewerAliases.ACTION_INACTIVATE_HEALTH_HISTORY_ALLERGIES)
  private readonly actionInactivateHealthHistoryAPI!: ActionAPI

  @Inject(HealthHistoryAllergiesViewerAliases.ACTION_REVIEW_HEALTH_HISTORY_ALLERGIES)
  private actionReviewHealthHistoryAPI!: ActionAPI;

  @Inject(HealthHistoryAllergiesViewerAliases.FORM_HEALTH_HISTORY_ALLERGIES_VIEWER)
  private readonly formHealthHistoryAllergiesAPI!: FormAPI;

  @Inject(HealthHistoryAllergiesViewerAliases.RADIO_GROUP_CLASSIFICATION)
  private readonly radioGroupClassificationAPI!: RadioGroupAPI;

  @Inject(HealthHistoryAllergiesViewerAliases.RADIO_GROUP_HIGHLIGHT_INTENSITY_SEVERITY)
  private readonly radioGroupHighlightIntensitySeverityAPI!: RadioGroupAPI;

  @Inject(HealthHistoryAllergiesViewerAliases.RADIO_GROUP_CONDITION)
  private readonly radioGroupConditionAPI!: RadioGroupAPI;

  @Inject(HealthHistoryAllergiesViewerAliases.FIELD_RECORD_DATE)
  private readonly fieldRecordDateAPI!: FieldAPI;

  @Inject(HealthHistoryAllergiesViewerAliases.FIELD_PROFESSIONAL)
  private readonly fieldProfessionalAPI!: FieldAPI;

  @Inject(HealthHistoryAllergiesViewerAliases.FIELD_HIGHLIGHT_INTENSITY_SEVERITY)
  private readonly fieldHighlightIntensitySeverityAPI!: FieldAPI;

  @Inject(HealthHistoryAllergiesViewerAliases.FIELD_ORIGIN)
  private readonly fieldOriginAPI!: FieldAPI;

  @Inject(HealthHistoryAllergiesViewerAliases.FIELD_CAUSATIVE_AGENT)
  private readonly fieldCausativeAgentAPI!: FieldAPI;

  @Inject(HealthHistoryAllergiesViewerAliases.FIELD_ACTIVE_INGREDIENT)
  private readonly fieldActieIngredientAPI!: FieldAPI;

  @Inject(HealthHistoryAllergiesViewerAliases.FIELD_DCB)
  private readonly fieldDcbAPI!: FieldAPI;

  @Inject(HealthHistoryAllergiesViewerAliases.FIELD_MATERIAL_MEDICINE)
  private readonly fieldMaterialAPI!: FieldAPI;

  @Inject(HealthHistoryAllergiesViewerAliases.FIELD_MATERIAL_MEDICINE_CLASS)
  private readonly fieldMaterialClassAPI!: FieldAPI;

  @Inject(HealthHistoryAllergiesViewerAliases.FIELD_FAMILY)
  private readonly fieldFamilyAPI!: FieldAPI;

  @Inject(HealthHistoryAllergiesViewerAliases.FIELD_REACTION_TYPE)
  private readonly fieldReactionTypeAPI!: FieldAPI;

  @Inject(HealthHistoryAllergiesViewerAliases.FIELD_CONDITION)
  private readonly fieldConditionAPI!: FieldAPI;

  @Inject(HealthHistoryAllergiesViewerAliases.FIELD_APPROACH)
  private readonly fieldApproachAPI!: FieldAPI;

  @Inject(HealthHistoryAllergiesViewerAliases.FIELD_SECURITY_LEVEL)
  private readonly fieldSecurityLevelAPI!: FieldAPI;

  @Inject(HealthHistoryAllergiesViewerAliases.FIELD_DATE_TYPE)
  private readonly fieldDateTypeAPI!: FieldAPI;

  @Inject(HealthHistoryAllergiesViewerAliases.FIELD_START_DATE)
  private readonly fieldStartDateAPI!: FieldAPI;

  @Inject(HealthHistoryAllergiesViewerAliases.FIELD_END_DATE)
  private readonly fieldEndDateAPI!: FieldAPI;

  @Inject(HealthHistoryAllergiesViewerAliases.FIELD_LAST_DATE)
  private readonly fieldLastDateAPI!: FieldAPI;

  @Inject(HealthHistoryAllergiesViewerAliases.FIELD_NOTES)
  private readonly fieldNotesAPI!: FieldAPI;

  @Inject(HealthHistoryAllergiesViewerAliases.FIELD_REVIEW_DATE)
  private readonly fieldReviewDateAPI!: FieldAPI;

  @Inject(HealthHistoryAllergiesViewerAliases.FIELD_REVIEW_USER)
  private fieldReviewUserAPI!: FieldAPI;

  @Inject(HealthHistoryAllergiesViewerAliases.FIELD_UNREGISTERED_CAUSATIVE_AGENT)
  private readonly fieldUnregisteredCausativeAgentAPI!: FieldAPI;

  @OnMount({ target: HealthHistoryAllergiesViewerAliases.LAYOUT_HEALTH_HISTORY_ALLERGIES_VIEWER })
  handleMountLayout():void {
    const { encounterId, patientId } = this.historyService.getCurrentRouteParams();
    applicationContext.popNavigationItem();
    InformationBarUtils.collapsedBar = true;
    this.context.set({encounterId, patientId});
    HealthHistoryListContext.currentTab = HealthHistoryType.ALLERGIES;
  }

  @OnMount({ target: HealthHistoryAllergiesViewerAliases.FORM_HEALTH_HISTORY_ALLERGIES_VIEWER })
  handleMountForm(): void {
    this.formHealthHistoryAllergiesAPI.setLoading(true);
    this.handleFetchAllergy()
  }

  @OnClick({ target: HealthHistoryAllergiesViewerAliases.ACTION_NEW_HEALTH_HISTORY_ALLERGIES })
  handleClickNew(): void {
    const { encounterId, patientId } = this.historyService.getCurrentRouteParams();
    applicationContext.redirect(ApplicationRoute.HEALTH_HISTORY_ALLERGIES_CREATE, {
      encounterId,
      patientId,
    });
  }

  @OnClick({ target: HealthHistoryAllergiesViewerAliases.ACTION_INACTIVATE_HEALTH_HISTORY_ALLERGIES })
  handleCLickInactivate(): void {
    const { idHealthHistory } = this.historyService.getCurrentRouteParams() as Record<string, Value>;
    const modalProperties: ModalProps = {
      closeable: false,
      closeByDocument: false,
      closeByEscape: false,
      focusOnOpen: false,
      fragment: 'health-history-inactivate',
      id: 'health-history-inactivate',
      onCancel: () => { },
      onSubmit: () => { },
      title: 'title.disable-record',
      uuid: generateUUID(),
    };

    modalProperties.contextData = {
      idHealthHistory,
    } as Record<string, Value>;

    ModalService.openDefault(modalProperties);
  }

  private handleReviewAllergy(): void {
    const { idHealthHistory } = this.historyService.getCurrentRouteParams() as Record<string, Value>;
    this.formHealthHistoryAllergiesAPI.setLoading(true);
    const body = {
      idHealthHistory
    } as Record<string, Value>;
    HttpService.put(Endpoint.REVIEW_HEALTH_HISTORY_ALLERGIES, undefined, { body })
      .then(() => {
        this.historyService.back();
        this.showToast('text.successfully-reviewed', ToastLevel.SUCCESS);
      }).catch(err => err?.body && this.showToast(err?.body, ToastLevel.ERROR))
      .finally(() => this.formHealthHistoryAllergiesAPI.setLoading(false))

  }

  @OnClick({ target: HealthHistoryAllergiesViewerAliases.ACTION_REVIEW_HEALTH_HISTORY_ALLERGIES })
  handleCLickReview(): void {
    DialogService.open({
      descriptionDialog: '',
      onClickCancel: () => { applicationContext.popNavigationItem()},
      onClickOk: () => { this.handleReviewAllergy()},
      titleDialog: 'text.confirm-review-item',
      titleOnCancel: 'tws.form.cancel',
      titleOnOk: 'text.review-item-hh',
    });
  }

  @OnUnmount(HealthHistoryAllergiesViewerAliases.LAYOUT_HEALTH_HISTORY_ALLERGIES_VIEWER)
  handleUnmount(): void {
    HealthHistoryListContext.clean();
    HealthHistoryListContext.currentTab = HealthHistoryType.ALLERGIES;
  }

  private handleVisibleFields(record: HealthHistoryAllergiesResponse): void {
    const isUserLocalePtBr = HealthHistoryAllergiesCommons.handleUserLocale();
    const isDcbVisible     = record.dcb != null && isUserLocalePtBr;

    this.fieldRecordDateAPI.setVisible(record.recordDate != null);
    this.fieldProfessionalAPI.setVisible(record.professional != null);
    this.fieldHighlightIntensitySeverityAPI.setVisible(record.highlightIntensitySeverity != null);
    this.fieldOriginAPI.setVisible(record.origin != null);
    this.fieldCausativeAgentAPI.setVisible(record.causativeAgent != null);
    this.fieldActieIngredientAPI.setVisible(record.activeIngredient != null);
    this.fieldDcbAPI.setVisible(isDcbVisible);
    this.fieldMaterialAPI.setVisible(record.material != null);
    this.fieldMaterialClassAPI.setVisible(record.materialClass != null);
    this.fieldFamilyAPI.setVisible(record.family != null);
    this.fieldReactionTypeAPI.setVisible(record.reactionType != null);
    this.fieldConditionAPI.setVisible(record.condition != null);
    this.fieldApproachAPI.setVisible(record.approach != null);
    this.fieldSecurityLevelAPI.setVisible(record.securityLevel != null);
    this.fieldDateTypeAPI.setVisible(record.dateType != null);
    this.fieldStartDateAPI.setVisible(record.startDate != null);
    this.fieldEndDateAPI.setVisible(record.endDate != null);
    this.fieldLastDateAPI.setVisible(record.lastDate != null);
    this.fieldNotesAPI.setVisible(record.notes != null);
    this.fieldReviewDateAPI.setVisible(record.reviewDate != null);
    this.fieldReviewUserAPI.setVisible(record.reviewUsername != null);
    this.fieldUnregisteredCausativeAgentAPI.setVisible(record.unregisteredCausativeAgent != null);
  }

  private getParamAccess(module: string, parameter: string): string {
    return FoundationUtils.findModuleParam(module, parameter) as string;
  }

  private defineEnablingReviewItem(): void {
    const isEnabledReview = this.getParamAccess(FunctionName.PEP_281, PEP_281Parameters.ALLOW_REVIEW_HEALTH_HISTORY);
    const allowInfo = isEnabledReview === VerifyReviewParameter.ALLOWED_REVIEW && InformationBarUtils.allowInfo && HealthHistoryListContext.isAllowEdit;
    this.actionReviewHealthHistoryAPI.setEnabled(allowInfo);
  }

  private handleFetchAllergy(): Promise<void> {
    const { idHealthHistory } = this.historyService.getCurrentRouteParams();
    const allowInfo = InformationBarUtils.allowInfo && HealthHistoryListContext.isAllowEdit;

    return HttpService.get(Endpoint.HEALTH_HISTORY_ALLERGIES, { sequenceNumberAllergy: idHealthHistory })
      .then(response => {
        const record = response.body as HealthHistoryAllergiesResponse;
        const user = foundationContext.getUser() as HealthProfessionalUser;
        const deniesAllergies = BooleanUtils.isTrueValue(record.deniesAllergies);
        const patientAlerts = BooleanUtils.isTrueValue(record.patientAlerts);
        const endDate = moment(record.endDate).local().format(FULL_LOCALE_DATE);
        const problemsList = BooleanUtils.isTrueValue(record.problemsList);
        const lastDate = moment(record.lastDate).utc().format(DATE_PATTERN);
        const recordDate = moment(record.recordDate).local().format(FULL_LOCALE_DATE);
        const startDate = moment(record.startDate).utc().format(DATE_PATTERN);
        const startMonth = moment(record.startMonth).utc().format(MONTH_YEAR_FORMAT);
        const dateType = record.dateType?.valueDomain as string;
        const reviewDate = moment(record.reviewDate).local().format(FULL_LOCALE_DATE);
        const showInactivate = record.professional?.id === user.physicalPersonId && Boolean(record.showInactivate) && allowInfo;

        const allData = {
          ...record,
          deniesAllergies,
          endDate,
          lastDate,
          patientAlerts,
          problemsList,
          recordDate,
          reviewDate,
          startDate,
          startMonth,
        };

        this.context.set({
          classification: record.classification,
          condition: record.condition,
          highlightIntensitySeverity: record.highlightIntensitySeverity
        });

        this.handleDateTypeFields(dateType);

        this.formHealthHistoryAllergiesAPI.setEditing(true);
        this.formHealthHistoryAllergiesAPI.validate();
        this.formHealthHistoryAllergiesAPI.setDisabled(true);
        this.formHealthHistoryAllergiesAPI.setData(allData);
        this.actionInactivateHealthHistoryAPI.setEnabled(showInactivate);

        this.handleRadioGroupData(AllergiesRadioGroupId.CLASSIFICATION, this.radioGroupClassificationAPI, AllergiesRadioGroupExpressions.CLASSIFICATION);
        this.handleRadioGroupData(AllergiesRadioGroupId.HIGHLIGHT_INTENSITY_SEVERITY, this.radioGroupHighlightIntensitySeverityAPI, AllergiesRadioGroupExpressions.HIGHLIGHT_INTENSITY_SEVERITY);
        this.handleRadioGroupData(AllergiesRadioGroupId.CONDITION, this.radioGroupConditionAPI, AllergiesRadioGroupExpressions.CONDITION);

        this.handleVisibleFields(record);
      }).catch((err) => err?.body && this.showToast(err?.body, ToastLevel.ERROR))
      .finally(() => {
        this.formHealthHistoryAllergiesAPI.setLoading(false);
        this.actionNewHealthHistoryAPI.setEnabled(allowInfo);
        this.defineEnablingReviewItem();
      });
  }

  private handleRadioGroupData(expressionId: number, radioGroup: RadioGroupAPI, type: string): void {
    const { encounterId, patientId } = this.historyService.getCurrentRouteParams();
    HttpService.get(Endpoint.GET_PAIN_SCALE_DATA, { encounterId, expressionId, patientId })
      .then(response => {
        const responseBody = response.body as SelectableItem[];
        radioGroup.setData(responseBody);

        this.handleRadioGroupValues(radioGroup, responseBody, type);
      }).catch((error) => {
        error?.body && this.showToast(error?.body, ToastLevel.ERROR)
      });
  }

  private handleRadioGroupValues(radioGroup: RadioGroupAPI, responseBody: SelectableItem[], type: string): void {
    const classification             = this.context.get('classification');
    const highlightIntensitySeverity = this.context.get('highlightIntensitySeverity');
    const condition                  = this.context.get('condition');

    switch (type) {
      case AllergiesRadioGroupExpressions.CLASSIFICATION:
        radioGroup.setValue(responseBody.find((field) => field.key === classification) as SelectableItem);
        break;
      case AllergiesRadioGroupExpressions.HIGHLIGHT_INTENSITY_SEVERITY:
        radioGroup.setValue(responseBody.find((field) => field.key === highlightIntensitySeverity) as SelectableItem);
        break;
      case AllergiesRadioGroupExpressions.CONDITION:
        radioGroup.setValue(responseBody.find((field) => field.key === condition) as SelectableItem);
        break;
      default:
        break;
    }
  }

  private handleDateTypeFields(valueDomain: string): void {
    let entry = AllergiesDateTypeEntry.DAY_MONTH_YEAR;

    switch (valueDomain) {
      case AllergiesDateTypeId.MONTH_YEAR:
        entry = AllergiesDateTypeEntry.MONTH_YEAR;
        break;
      case AllergiesDateTypeId.YEAR:
        entry = AllergiesDateTypeEntry.YEAR;
        break;
    }

    this.fieldStartDateAPI.setEntry(entry);
  }

  private showToast(message: string, level: ToastLevel): void {
    ToastService.open({
      dismissible: true,
      highlight: false,
      id: 'health-history-allergies-viewer',
      level,
      message,
      placement: Placement.BOTTOM,
      showIcon: true,
      timeout: 5000,
      type: ToastType.BANNER,
    })
  }
}
